import PropTypes from 'prop-types';
import { useState, useCallback, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  makeStyles,
  Checkbox,
  Typography,
  Box,
  Divider,
  CircularProgress,
} from '@material-ui/core';
import Close from '@material-ui/icons/Close';
import { ADD_MESSAGE } from 'util/actions';
import {
  createGuestPayerAndJwt,
  closePaymentIframe,
  selector as palGuestPaymentSelector,
} from 'model/palGuestPayment';
import { selectedFspIdSelector } from 'selectors/fsp';
import { selector as fspConfigPayALoanSelector } from 'selectors/fspConfigPayALoan';
import normalizeAssertionBaseUrl from 'util/normalizeAssertionBaseUrl';
import { SEARCH_PATH } from '../paths';
import PaymentIframe from './PaymentIframe';
import { buildPalGuestPaymentPopupFeatures } from './popupConstants';

const PAYMENT_EXIT_MESSAGE =
  'You can use Search to find the guest payer and view scheduled payments.';

function getPopupLoadingUrl() {
  return `${window.location.origin}/palGuestPaymentLoading.html`;
}

const useStyle = makeStyles((theme) => ({
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
  },
  content: {
    paddingTop: theme.spacing(2),
  },
  infoSection: {
    marginBottom: theme.spacing(3),
  },
  infoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: theme.spacing(1, 0),
  },
  infoLabel: {
    fontWeight: 500,
    color: theme.palette.text.secondary,
  },
  infoValue: {
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  successText: {
    color: theme.palette.success.main,
    fontWeight: 500,
    marginBottom: theme.spacing(2),
  },
  errorText: {
    color: theme.palette.error.main,
    fontWeight: 500,
    marginBottom: theme.spacing(2),
  },
  divider: {
    margin: theme.spacing(2, 0),
  },
  checkboxLabel: {
    marginTop: theme.spacing(2),
  },
  statusContainer: {
    textAlign: 'center',
    padding: theme.spacing(5, 3, 3),
  },
  promptQuestion: {
    fontWeight: 600,
    color: theme.palette.text.primary,
    lineHeight: 1.4,
  },
  popupBlockedBox: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.warning.light,
    borderRadius: theme.shape.borderRadius,
    color: theme.palette.warning.dark,
  },
  popupBlockedList: {
    textAlign: 'left',
    display: 'inline-block',
    margin: theme.spacing(1, 0),
  },
  popupBlockedButton: {
    marginTop: theme.spacing(2),
  },
  bringForwardButton: {
    marginTop: theme.spacing(2),
  },
}));

const ConfirmDialog = ({ open, onClose, loanResult }) => {
  const classes = useStyle();
  const dispatch = useDispatch();
  const history = useHistory();
  const fspId = useSelector(selectedFspIdSelector);
  const palGuestPayment = useSelector(palGuestPaymentSelector);
  const fspConfigPayALoan = useSelector((state) =>
    fspConfigPayALoanSelector(state, fspId)
  );
  const apiError = palGuestPayment?.error;
  const [acceptedConfirm, setAcceptedConfirm] = useState(false);
  const [isPopupBlocked, setIsPopupBlocked] = useState(false);
  const preOpenedPopupRef = useRef(null);
  const paymentPopupWindowRef = useRef(null);
  const paymentExitHandledRef = useRef(false);

  useEffect(() => {
    if (open) {
      paymentExitHandledRef.current = false;
    }
  }, [open]);

  const toggleAcceptedConfirm = () => {
    setAcceptedConfirm(!acceptedConfirm);
  };

  const resetDialog = useCallback(() => {
    if (
      preOpenedPopupRef.current &&
      typeof preOpenedPopupRef.current.close === 'function'
    ) {
      try {
        preOpenedPopupRef.current.close();
      } catch (_e) {
        // ignore if already closed
      }
      preOpenedPopupRef.current = null;
    }
    paymentPopupWindowRef.current = null;
    setAcceptedConfirm(false);
    setIsPopupBlocked(false);
    dispatch(closePaymentIframe());
  }, [dispatch]);

  const handlePopupReady = useCallback((popupWindow) => {
    paymentPopupWindowRef.current = popupWindow;
  }, []);

  const handleBringPaymentToFront = useCallback(() => {
    const p = paymentPopupWindowRef.current;
    if (!p || p.closed) return;
    try {
      p.focus();
    } catch (_e) {
      // ignore
    }
  }, []);

  const exitToSearch = useCallback(() => {
    if (paymentExitHandledRef.current) {
      return;
    }
    paymentExitHandledRef.current = true;
    dispatch({
      type: ADD_MESSAGE.ACTION,
      data: PAYMENT_EXIT_MESSAGE,
    });
    resetDialog();
    onClose();
    history.push(SEARCH_PATH);
  }, [dispatch, history, onClose, resetDialog]);

  const handlePopupClosed = useCallback(() => {
    exitToSearch();
  }, [exitToSearch]);

  const handleCancel = useCallback(
    (_, reason) => {
      if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
        return;
      }
      resetDialog();
      onClose();
    },
    [onClose, resetDialog]
  );

  const handleMakePayment = useCallback(() => {
    if (!loanResult || !fspId) {
      return;
    }

    const { loanNumber, accountSuffix, email } = loanResult;

    if (!loanNumber || !email) {
      return;
    }

    const popup = window.open(
      getPopupLoadingUrl(),
      'PALGuestPayment',
      buildPalGuestPaymentPopupFeatures()
    );

    if (!popup || popup.closed || popup.closed === undefined) {
      setIsPopupBlocked(true);
      return;
    }

    preOpenedPopupRef.current = popup;
    try {
      popup.focus();
    } catch (_e) {
      // ignore
    }
    setIsPopupBlocked(false);

    const assertionBaseUrl = normalizeAssertionBaseUrl(
      fspConfigPayALoan?.guestUrl
    );

    dispatch(
      createGuestPayerAndJwt({
        fspId,
        loanNumber,
        accountSuffix: accountSuffix || '',
        email,
        assertionBaseUrl,
      })
    );
  }, [dispatch, loanResult, fspId, fspConfigPayALoan]);

  useEffect(() => {
    if (
      apiError &&
      preOpenedPopupRef.current &&
      typeof preOpenedPopupRef.current.close === 'function'
    ) {
      try {
        preOpenedPopupRef.current.close();
      } catch (_e) {
        // ignore if already closed
      }
      preOpenedPopupRef.current = null;
    }
  }, [apiError]);

  if (!loanResult) return null;

  const isSuccess =
    (loanResult.verificationStatus || '').toLowerCase() === 'success';

  const isLoading = palGuestPayment?.loading || false;

  const hasPaymentData =
    palGuestPayment?.paymentIframe?.jwt &&
    palGuestPayment?.paymentIframe?.fspId &&
    palGuestPayment?.paymentIframe?.assertionBaseUrl;

  const isInPaymentFlow = Boolean(hasPaymentData);

  const dialogTitle = isInPaymentFlow
    ? 'PAL Guest Loan Payment'
    : 'Confirm PAL Guest Loan Payment';

  let dialogContent;
  let dialogActions = null;

  if (isInPaymentFlow) {
    dialogContent = (
      <>
        <Box className={classes.statusContainer}>
          <Typography className={classes.promptQuestion} gutterBottom>
            Complete your payment in the popup window.
          </Typography>
          <Typography variant="body2" color="textSecondary" paragraph>
            The payment stays in a separate window centered on this screen.
            Ontrac will keep bringing it to the front while it stays open. Use
            the button below if you need to focus it while using this dialog
            (for example to close it).
          </Typography>
          <Button
            type="button"
            variant="outlined"
            color="primary"
            className={classes.bringForwardButton}
            onClick={handleBringPaymentToFront}
          >
            Bring payment window to front
          </Button>
        </Box>
        <PaymentIframe
          jwt={palGuestPayment.paymentIframe.jwt}
          fspId={palGuestPayment.paymentIframe.fspId}
          assertionBaseUrl={palGuestPayment.paymentIframe.assertionBaseUrl}
          onPopupClosed={handlePopupClosed}
          onPopupReady={handlePopupReady}
          preOpenedPopup={preOpenedPopupRef.current}
        />
      </>
    );
    dialogActions = null;
  } else if (isSuccess) {
    dialogContent = (
      <>
        <Typography variant="h6" className={classes.successText} gutterBottom>
          Verification Successful
        </Typography>

        <Box className={classes.infoSection}>
          <Box className={classes.infoRow}>
            <Typography className={classes.infoLabel}>Loan Number:</Typography>
            <Typography className={classes.infoValue}>
              {loanResult.loanNumber || 'N/A'}
            </Typography>
          </Box>
          {loanResult.accountSuffix && (
            <Box className={classes.infoRow}>
              <Typography className={classes.infoLabel}>
                Account Suffix:
              </Typography>
              <Typography className={classes.infoValue}>
                {loanResult.accountSuffix}
              </Typography>
            </Box>
          )}
          <Divider className={classes.divider} />
        </Box>

        <FormControlLabel
          className={classes.checkboxLabel}
          control={
            <Checkbox
              checked={acceptedConfirm}
              color="primary"
              onClick={toggleAcceptedConfirm}
              disabled={isLoading}
            />
          }
          label="By checking this box, you have confirmed that the loan information is correct and you are authorized to proceed with the payment."
        />

        {apiError && (
          <Box mt={2}>
            <Typography color="error" variant="body2">
              {apiError}
            </Typography>
          </Box>
        )}

        {isPopupBlocked && (
          <Box className={classes.popupBlockedBox} mt={2}>
            <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
              Popup Permission Required
            </Typography>
            <Typography variant="body2" component="div">
              Your browser blocked the payment window. To continue:
            </Typography>
            <ol className={classes.popupBlockedList}>
              <li>Allow popups for this site (address bar or site settings)</li>
              <li>Click Try Again below</li>
            </ol>
            <Button
              className={classes.popupBlockedButton}
              variant="contained"
              color="primary"
              onClick={handleMakePayment}
            >
              Try Again
            </Button>
          </Box>
        )}
      </>
    );
    dialogActions = (
      <DialogActions>
        <Button
          color="primary"
          disabled={!acceptedConfirm || isLoading}
          onClick={handleMakePayment}
        >
          {isLoading ? (
            <>
              <CircularProgress size={20} style={{ marginRight: 8 }} />
              Processing...
            </>
          ) : (
            'Make Payment'
          )}
        </Button>
      </DialogActions>
    );
  } else {
    dialogContent = (
      <>
        <Typography variant="h6" className={classes.errorText} gutterBottom>
          Verification Failed
        </Typography>

        {loanResult.verificationStatusReason && (
          <Typography variant="body2" color="textSecondary">
            {loanResult.verificationStatusReason}
          </Typography>
        )}
      </>
    );
  }

  return (
    <Dialog
      maxWidth="sm"
      fullWidth
      onClick={(event) => event.stopPropagation()}
      open={open}
      onClose={handleCancel}
      disableEscapeKeyDown
      disableEnforceFocus={isInPaymentFlow}
      disableAutoFocus={isInPaymentFlow}
    >
      <DialogTitle>
        {dialogTitle}
        <IconButton
          aria-label="Close Dialog"
          className={classes.closeButton}
          color="inherit"
          onClick={handleCancel}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent className={classes.content}>{dialogContent}</DialogContent>
      {dialogActions}
    </Dialog>
  );
};

ConfirmDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  loanResult: PropTypes.shape({
    operationId: PropTypes.string,
    loanNumber: PropTypes.string,
    accountSuffix: PropTypes.string,
    email: PropTypes.string,
    verificationStatus: PropTypes.string,
    verificationStatusReason: PropTypes.string,
    accountType: PropTypes.string,
    loanType: PropTypes.string,
    statementDate: PropTypes.string,
    dueDate: PropTypes.string,
    ledgerBalance: PropTypes.string,
    statementBalance: PropTypes.string,
    minimumAmountDue: PropTypes.string,
    lateFee: PropTypes.string,
    statementType: PropTypes.string,
    interestRate: PropTypes.string,
  }),
};

ConfirmDialog.defaultProps = {
  loanResult: null,
};

export default ConfirmDialog;
